import { z } from 'zod';

export const FieldKind = z.enum([
  'text',
  'longText',
  'number',
  'select',
  'multiselect',
  'severity',
  'photos',
  'voice',
  'boolean',
  'date',
  'signature',
]);

export const FormFieldDef = z.object({
  id: z.string(),
  sectionId: z.string(),
  kind: FieldKind,
  label: z.string(),
  required: z.boolean().default(false),
  /** Field-specific config — options, min/max, units. */
  config: z.record(z.unknown()).default({}),
});
export type FormFieldDef = z.infer<typeof FormFieldDef>;

export const FormSectionDef = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number().int(),
});
export type FormSectionDef = z.infer<typeof FormSectionDef>;

export const FormTemplate = z.object({
  id: z.string(),
  name: z.string(),
  version: z.string(),
  sections: z.array(FormSectionDef),
  fields: z.array(FormFieldDef),
  usageCount: z.number().int().nonnegative().default(0),
  /**
   * Template-wide settings, in the same free-form shape a field's config has.
   *
   * `subjectNoun` is the first: what this customer calls the things they
   * inspect, so a report of a forty-door walk reads "Fire door D-114" rather
   * than "D-114". Presentation only — it never claims the code resolves to
   * anything, and a template that sets none gets the bare code rather than a
   * noun the product guessed.
   *
   * Free-form for the same reason `FormFieldDef.config` is: domain vocabulary
   * belongs to the customer who wrote the template, not to this schema. A
   * typed field per setting would mean shipping a release to let one org call
   * their things gantries.
   *
   * Mirrored by `TemplateDefinition.config` in the backend's
   * `schemas/template.py`. Zod strips unknown keys, so without the field on
   * both sides a template could carry a noun the phone silently discards on
   * pull — which is exactly what happened before this existed.
   */
  config: z.record(z.unknown()).default({}),
});
export type FormTemplate = z.infer<typeof FormTemplate>;

export const FieldValue = z.object({
  fieldId: z.string(),
  value: z.unknown(),
  /**
   * Where this value came from — drives the "Source" link in the UI.
   *
   * `video-ai` is its own value rather than reusing `photo-ai`: this field is
   * a provenance claim, and telling a reviewer a number came from a
   * photograph when it came from a ten-second clip with narration over it
   * makes the audit trail say something untrue. Adding a member here also
   * needs `FieldValueSource` in the backend's `core/constants.py` and a
   * migration recreating `field_values_source_check`, or the value 422s.
   *
   * `scan` is here for the same reason. A code read off a label by a decoder
   * is not a code someone keyed, and typos are precisely what scanning
   * removes — a reviewer who sees `manual` on an asset id will assume a human
   * typed it and weigh it accordingly. It is not `photo-ai` either: no model
   * was involved and there is nothing probabilistic about it.
   */
  source: z
    .enum(['manual', 'scan', 'voice', 'photo-ai', 'video-ai', 'exif', 'template', 'reviewer'])
    .default('manual'),
  /** Confidence [0,1] for AI-filled values. */
  confidence: z.number().min(0).max(1).nullable().default(null),
  /** Capture id this value was extracted from, if any. */
  sourceCaptureId: z.string().nullable().default(null),
  updatedAt: z.string().datetime(),
});
export type FieldValue = z.infer<typeof FieldValue>;
